import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles, User, LogOut, ChevronRight, X } from 'lucide-react';
import { useAuth } from '@/domains/auth';
import { navigateTo } from '@/shared/utils/navigation';
import './NavbarMobileMenu.css';

const SOLUTION_ITEMS = [
  {
    icon: Sparkles,
    title: 'AI 실시간 자세 분석',
    description: '웹캠 & 영상 33개 관절 스켈레톤 추론',
    href: '/features',
    featured: true,
  },
];

export function NavbarMobileMenu({ isOpen, onClose }) {
  const { user, isAuthenticated, logout } = useAuth();

  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleNav = (e, path) => {
    e.preventDefault();
    onClose();
    navigateTo(path);
  };

  const displayName =
    user?.user_metadata?.name || user?.email?.split('@')[0] || '궁사';

  const drawerContent = (
    <div
      className="navbar-mobile-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="모바일 메뉴"
    >
      <div className="navbar-mobile-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="navbar-mobile-drawer__header">
          <span className="navbar-mobile-drawer__heading">AIMERS 메뉴</span>
          <button
            type="button"
            className="navbar-mobile-close-btn"
            onClick={onClose}
            aria-label="메뉴 닫기"
          >
            <X size={20} />
          </button>
        </div>

        <div className="navbar-mobile-drawer__section">
          <span className="navbar-mobile-drawer__label">솔루션 & 기능</span>
          <div className="navbar-mobile-drawer__links">
            {SOLUTION_ITEMS.map((item, idx) => {
              const Icon = item.icon;
              return (
                <a
                  key={idx}
                  href={item.href}
                  className={`navbar-mobile-item ${item.featured ? 'navbar-mobile-item--featured' : ''}`}
                  onClick={(e) => handleNav(e, item.href)}
                >
                  <div className={`navbar-mobile-item__icon-box ${item.featured ? 'navbar-mobile-item__icon-box--featured' : ''}`}>
                    <Icon size={18} />
                  </div>
                  <div className="navbar-mobile-item__content">
                    <strong className="navbar-mobile-item__title">{item.title}</strong>
                    <span className="navbar-mobile-item__desc">{item.description}</span>
                  </div>
                  <ChevronRight size={16} className="navbar-mobile-item__arrow" />
                </a>
              );
            })}
          </div>
        </div>

        <div className="navbar-mobile-drawer__section">
          <span className="navbar-mobile-drawer__label">바로가기</span>
          <div className="navbar-mobile-drawer__quick-links">
            <a href="#community" className="navbar-mobile-quick-link" onClick={onClose}>
              커뮤니티
            </a>
            <a href="#download" className="navbar-mobile-quick-link" onClick={onClose}>
              앱 다운로드
            </a>
            <a
              href="mailto:aimus1440@gmail.com?subject=%5BAIMERS%5D%20%EC%A0%84%EB%AC%B8%20%ED%85%8C%EC%8A%A4%ED%84%B0%EB%8B%A8%20%EC%A7%80%EC%9B%90"
              className="navbar-mobile-quick-link navbar-mobile-quick-link--highlight"
              onClick={onClose}
            >
              🎯 테스터단 지원
            </a>
          </div>
        </div>

        <div className="navbar-mobile-drawer__footer">
          {isAuthenticated ? (
            <div className="navbar-mobile-user">
              <div className="navbar-mobile-user__info">
                <span className="navbar-mobile-user__name">{displayName} 님</span>
                <span className="navbar-mobile-user__email">{user?.email}</span>
              </div>
              <button
                type="button"
                className="navbar-mobile-logout-btn"
                onClick={() => {
                  logout();
                  onClose();
                }}
              >
                <LogOut size={15} />
                <span>로그아웃</span>
              </button>
            </div>
          ) : (
            <a
              href="/login"
              className="navbar-mobile-login-btn"
              onClick={(e) => handleNav(e, '/login')}
            >
              <User size={16} />
              <span>로그인 / 회원가입</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(drawerContent, document.body)
    : drawerContent;
}


